import { Component, Inject, OnDestroy } from "@angular/core";
import { Subject, Subscription, takeUntil } from "rxjs";
import { AssessmentTaskScopeChangesService } from "../../services/assessment_task_scope_changes.service";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { AccountSettingsService } from "../../services/account_settings.service";
import { AssessmentAssignmentResult, AssessmentAssignmentRole, UserAccessContext } from "../../services/assessment_user_access.types";
import { LoginService } from "../../services/login.service";
import { assessmentTaskSessionIdentity } from "../../services/assessment_task_session.util";
import { ASSIGNMENT_ROLE_CHOICES, assignmentMutationMessage, assignmentSelectionError } from "./assessment_assignment_form.util";

export interface UserAssignmentModalData {
    access: UserAccessContext;
    productLicenseId: number;
    targetUserId: number;
    sessionIdentity: string;
    token: string;
    isCurrentOrigin?: () => boolean;
}
export type UserAssignmentCompletion = { outcome: "canceled" } | { outcome: "assignments_saved"; assignment: AssessmentAssignmentResult };

@Component({
    selector: "app-user-assignment-modal",
    template: `
        <p><strong>{{ data.access.target?.displayName }}</strong> · Organization role: {{ data.access.target?.organizationRole }}</p>
        <p>Profile fields are read-only here. Assessment access does not grant permission to edit a profile.</p>
        <p>Assessment: <strong>{{ data.access.assessment?.displayName }}</strong> · {{ data.access.assessment?.merchantName }}</p>
        <app-assessment-assignment-form [assessment]="data.access.assessment" [(roles)]="roles"
            [organizationRole]="data.access.target!.organizationRole" [targetUserId]="data.targetUserId"
            [disabled]="busy || accessLoading || !canManage"></app-assessment-assignment-form>
        <p>Clearing every role removes this member's assigned access to this assessment, including your own or the last editor assignment.</p>
        <p *ngIf="!canManage">These assignments are read-only with your current permissions.</p>
        <h3 *ngIf="otherAssignments.length">Other assessments — read-only</h3>
        <ul><li *ngFor="let assignment of otherAssignments">{{ assignment.displayName }} · {{ assignment.merchantName }}: {{ roleLabels(assignment.assignmentRoles) }}</li></ul>
        <p *ngIf="error" role="alert" class="cb4-dialog-error">{{ error }}</p>
        <p>Save changes only this assessment's access. It does not save the originating task, evidence item or assignment form.</p>
        <div class="actions">
            <button nz-button type="button" [disabled]="busy" (click)="close()">Cancel</button>
            <button nz-button nzType="primary" type="button" [disabled]="!canSave" [nzLoading]="busy" (click)="save()">Save assessment access</button>
        </div>
    `,
    styles: [`.actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }`]
})
export class UserAssignmentModalComponent implements OnDestroy {
    roles: AssessmentAssignmentRole[] = [...(this.data.access.assessment?.assignmentRoles ?? [])];
    readonly otherAssignments = (this.data.access.target?.assessmentAssignments ?? []).filter((row) => row.productLicenseId !== this.data.productLicenseId);
    busy = false;
    accessLoading = false;
    private accessFailed = false;
    private refreshPending = false;
    private accessRead?: Subscription;
    private readVersion = 0;
    error = "";
    private readonly destroyed = new Subject<void>();
    constructor(@Inject(NZ_MODAL_DATA) readonly data: UserAssignmentModalData, private readonly accounts: AccountSettingsService,
        private readonly login: LoginService, private readonly modal: NzModalRef<UserAssignmentModalComponent, UserAssignmentCompletion>,
        scopeChanges: AssessmentTaskScopeChangesService) {
        scopeChanges.watchChanges().pipe(takeUntil(this.destroyed)).subscribe((change) => {
            if (change.productLicenseId !== null && change.productLicenseId !== this.data.productLicenseId) return;
            this.refreshPending = true;
            this.refreshAccess();
        });
    }
    private refreshAccess(): void {
        if (!this.refreshPending || this.busy || !this.current()) return;
        this.refreshPending = false;
        const version = ++this.readVersion;
        this.accessRead?.unsubscribe();
        this.accessLoading = true;
        this.accessRead = this.accounts.getUserAccess({ productLicenseId: this.data.productLicenseId, targetUserId: this.data.targetUserId })
            .pipe(takeUntil(this.destroyed)).subscribe({
                next: (access) => {
                    if (version !== this.readVersion || !this.current()) return;
                    this.accessLoading = false;
                    this.accessFailed = false;
                    this.data.access = access;
                    if (!this.canManage) this.error = "Assessment assignment permission is no longer available. Your draft is preserved and cannot be saved.";
                },
                error: () => {
                    if (version !== this.readVersion || !this.current()) return;
                    this.accessLoading = false;
                    this.accessFailed = true;
                    this.error = "Current assessment assignment permission could not be confirmed. Close and reopen this dialog; your changes have not been submitted.";
                }
            });
    }
    get canManage(): boolean {
        return this.data.access.target?.userId === this.data.targetUserId && this.data.access.assessment?.productLicenseId === this.data.productLicenseId &&
            this.data.access.target.capabilities.canManageAssessmentAssignments && !this.data.access.assessment.readOnly;
    }
    get canSave(): boolean {
        return !this.busy && !this.accessLoading && !this.accessFailed && !this.refreshPending && this.canManage && !assignmentSelectionError(this.data.access.assessment, this.roles,
            this.data.access.target!.organizationRole, this.data.targetUserId, false);
    }
    roleLabels(roles: AssessmentAssignmentRole[]): string {
        return roles.map((role) => ASSIGNMENT_ROLE_CHOICES.find((choice) => choice.value === role)?.label || role).join(", ");
    }
    save(): void {
        if (!this.current() || !this.canSave) return;
        this.busy = true; this.error = "";
        this.modal.updateConfig({ nzKeyboard: false, nzClosable: false });
        this.accounts.setUserAssessmentAssignments(this.data.targetUserId, this.data.productLicenseId, [...this.roles])
            .pipe(takeUntil(this.destroyed)).subscribe({
                next: (assignment) => {
                    if (this.current()) this.modal.destroy({ outcome: "assignments_saved", assignment });
                },
                error: (error: unknown) => {
                    if (!this.current()) return;
                    this.busy = false; this.error = assignmentMutationMessage(error);
                    this.modal.updateConfig({ nzKeyboard: true, nzClosable: true });
                    this.refreshAccess();
                }
            });
    }
    close(): void { if (!this.busy) this.modal.destroy({ outcome: "canceled" }); }
    private current(): boolean {
        if (this.data.sessionIdentity === assessmentTaskSessionIdentity(this.login.getUserSnapshot()) && this.data.token === this.login.getToken() &&
            (this.data.isCurrentOrigin?.() ?? true)) return true;
        this.modal.destroy({ outcome: "canceled" }); return false;
    }
    ngOnDestroy(): void { this.readVersion += 1; this.accessRead?.unsubscribe(); this.destroyed.next(); this.destroyed.complete(); }
}
